import React, { useState, useEffect, useRef } from "react";
import { Send, MessageSquare, User, ShieldAlert } from "lucide-react";
import { collection, addDoc, query, orderBy, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";

export default function TicketChat({ ticket, currentUserName, currentUserRoles }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!ticket?.id) return;
    const q = query(
      collection(db, "tickets", ticket.id, "chats"),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setMessages(list);
    }, (err) => {
      console.error("Error subscribing to ticket chats:", err);
    });

    return () => unsubscribe();
  }, [ticket?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || submitting) return;

    const messageText = newMessage.trim();
    setNewMessage("");

    try {
      setSubmitting(true);

      // 1. Add message to the subcollection
      await addDoc(collection(db, "tickets", ticket.id, "chats"), {
        message: messageText,
        senderName: currentUserName || "System User",
        senderRoles: currentUserRoles || [],
        createdAt: new Date().toISOString()
      });

      // 2. Notifications trigger logic
      const isDeveloper = (currentUserRoles || []).includes("Developer");

      // Update parent ticket metadata to track unread chats
      const parentUpdates = {
        lastMessageAt: new Date().toISOString(),
        lastMessageSender: currentUserName || "System User",
        lastMessageReadBy: currentUser ? [currentUser.uid] : []
      };

      if (ticket.status !== "Complete") {
        if (isDeveloper) {
          parentUpdates.status = "Waiting";
        } else {
          parentUpdates.status = "Responded";
        }
      }

      await updateDoc(doc(db, "tickets", ticket.id), parentUpdates);
      
      if (isDeveloper) {
        // Send a targeted notification alert to the creator
        if (ticket.createdBy && ticket.createdBy !== currentUserName) {
          await addDoc(collection(db, "notifications"), {
            key: `ticket_chat_${ticket.id}_${Date.now()}`,
            message: `Developer Alert: New message in Ticket ${ticket.ticketNumber} from developer.`,
            type: "ticket_chat",
            ticketId: ticket.id,
            ticketNumber: ticket.ticketNumber,
            path: `/profile?tab=tickets&ticketId=${ticket.id}&ticketNumber=${ticket.ticketNumber}`,
            targetUser: ticket.createdBy,
            createdAt: new Date().toISOString(),
            readBy: []
          });
        }
      } else {
        // Creator responds: send an alert notification to all Developers
        await addDoc(collection(db, "notifications"), {
          key: `ticket_chat_${ticket.id}_${Date.now()}`,
          message: `Support Alert: Submitter ${ticket.createdBy} responded in Ticket ${ticket.ticketNumber}.`,
          type: "ticket_chat",
          ticketId: ticket.id,
          ticketNumber: ticket.ticketNumber,
          path: `/support?ticketId=${ticket.id}&ticketNumber=${ticket.ticketNumber}`,
          targetRoles: ["Developer"],
          createdAt: new Date().toISOString(),
          readBy: []
        });
      }
    } catch (err) {
      console.error("Error sending ticket chat message:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ 
      border: "1px solid var(--border-color)", 
      borderRadius: "8px", 
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      overflow: "hidden",
      marginTop: "0.5rem"
    }}>
      {/* Thread Header */}
      <div style={{ 
        padding: "0.75rem 1rem", 
        borderBottom: "1px solid var(--border-color)", 
        backgroundColor: "rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem"
      }}>
        <MessageSquare size={16} className="text-secondary" />
        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)" }}>
          Ticket Conversation Thread
        </span>
      </div>

      {/* Messages Stream */}
      <div style={{ 
        maxHeight: "260px", 
        overflowY: "auto", 
        padding: "1rem", 
        display: "flex", 
        flexDirection: "column", 
        gap: "0.75rem"
      }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)", fontSize: "0.85rem", fontStyle: "italic" }}>
            No chat history. Send a message to start the thread.
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderName === currentUserName;
            const isDev = (msg.senderRoles || []).includes("Developer");
            return (
              <div 
                key={msg.id} 
                style={{ 
                  alignSelf: isMe ? "flex-end" : "flex-start",
                  maxWidth: "75%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isMe ? "flex-end" : "flex-start",
                  gap: "0.25rem"
                }}
              >
                {/* Message Bubble */}
                <div style={{ 
                  backgroundColor: isMe ? "var(--primary)" : "rgba(255, 255, 255, 0.05)",
                  color: isMe ? "#ffffff" : "var(--text-primary)",
                  border: isMe ? "none" : "1px solid var(--border-color)",
                  padding: "0.6rem 0.9rem",
                  borderRadius: "12px",
                  borderBottomRightRadius: isMe ? "0" : "12px",
                  borderBottomLeftRadius: isMe ? "12px" : "0",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                  wordBreak: "break-word"
                }}>
                  {msg.message}
                </div>
                
                {/* Meta details */}
                <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <span>{msg.senderName}</span>
                  {isDev && (
                    <span style={{ 
                      fontSize: "0.6rem", 
                      backgroundColor: "rgba(33, 150, 243, 0.2)", 
                      color: "#2196f3", 
                      padding: "0.05rem 0.25rem", 
                      borderRadius: "4px",
                      fontWeight: 600
                    }}>
                      Dev
                    </span>
                  )}
                  <span>•</span>
                  <span>{msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</span>
                </span>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Box */}
      <form onSubmit={handleSendMessage} style={{ 
        borderTop: "1px solid var(--border-color)", 
        padding: "0.75rem", 
        display: "flex", 
        gap: "0.5rem",
        backgroundColor: "rgba(0,0,0,0.1)"
      }}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Ask for details or respond to support..."
          className="form-input"
          style={{ margin: 0, padding: "0.5rem 0.75rem", fontSize: "0.85rem", flex: 1 }}
          required
          disabled={submitting}
        />
        <button 
          type="submit" 
          className="btn btn-primary"
          style={{ padding: "0.5rem 0.75rem", display: "flex", alignItems: "center", justifyContent: "center" }}
          disabled={submitting || !newMessage.trim()}
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
